import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import { setComplianceCenterSelectedStandard } from '../../utils/complianceStandard'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

export type StandardReadinessBlock = {
  selfAssessed: number
  verified: number
  auditReadiness: number | null
  assessed: boolean
}

export type ExecutiveSummaryData = {
  openRisks: number
  executiveOpenRisks?: number
  openComplianceGaps?: number
  openComplianceGapsBreakdown?: { total?: number; byStandard?: Record<string, number> }
  internalOperationalObservations?: number
  internalOperationalObservationsBreakdown?: {
    total?: number
    byStandard?: Record<string, number>
    bySourceType?: Record<string, number>
    examples?: Array<{ id?: string; title?: string; standardCode?: string | null }>
  }
  complianceGapObservations?: number
  totalRiskRelatedItems?: number
  highPriorityRisks?: number
  criticalRisks: number
  highOrCriticalRisks?: number
  openRecommendations: number
  highPriorityRecommendations: number
  overdueRecommendations: number
  overdueRemediation?: number
  failedControls: number
  auditGaps: number
  openFindings?: number
  overallCompliance?: number
  verifiedCompliance?: number
  auditReadiness?: number | null
  readinessScore?: number
  compliance?: {
    overallCompliance: number
    verifiedCompliance: number
    auditReadiness: number | null
    readinessScore: number
  }
  standards?: Record<string, StandardReadinessBlock>
  isoReadiness: number
  cisReadiness: number
  nis2Readiness: number
  vulnerabilities: { critical: number; high: number; medium: number; low: number }
  executivePosture?: string
  rootCauses?: Array<{
    controlKey: string
    label?: string
    observationCount: number
    standardCode?: string | null
  }>
  rootCauseSummary?: string
  certificationBlockers?: Array<{ type: string; count: number; label: string }>
  readinessDrivers?: Array<{
    controlKey: string
    label?: string
    impactPercent: number
  }>
  remediationPriorities?: Array<{
    controlKey?: string
    label?: string
    title?: string
    riskReduction?: number
    readinessGain?: number
    priorityScore?: number
  }>
  highestImpactActions?: Array<{
    controlKey?: string
    label?: string
    title?: string
    standardCode?: string | null
    riskReduction?: number
    readinessGain?: number
    priorityScore?: number
  }>
  criticalControlActions?: Array<{
    controlKey?: string
    label?: string
    standardCode?: string | null
    riskReduction?: number
    readinessGain?: number
    priorityScore?: number
    observationCount?: number
    isFailedControl?: boolean
  }>
  riskConcentration?: {
    domains?: Array<{ domain: string; percent: number }>
    primaryRiskDomains?: Array<{ domain: string; percent: number }>
  }
  executiveInsights?: string[]
  improvementScenario?: {
    currentReadiness: number
    projectedReadiness: number
    projectedReadinessRange?: { low: number; high: number }
    risksReduced: number
    blockersRemoved: number
    topActions?: Array<{
      priority?: number
      controlKey?: string
      label?: string
      standardCode?: string | null
      readinessGain?: number
      riskReduction?: number
    }>
    criticalControlActions?: Array<{
      priority?: number
      controlKey?: string
      label?: string
      standardCode?: string | null
      readinessGain?: number
      riskReduction?: number
      priorityScore?: number
    }>
  }
  certificationPath?: {
    currentReadiness: number
    targetReadiness: number
    recommendedActions?: Array<{
      controlKey?: string
      standardCode?: string | null
      label?: string
      readinessGain?: number
      riskReduction?: number
      priorityScore?: number
    }>
    expectedReadinessAfterCompletion: number
    expectedReadinessRange?: { low: number; high: number }
    blockersRemoved?: number
    risksReduced?: number
  }
  /** Authoritative readiness model for Certification path card (one forecast source). */
  executiveReadiness?: {
    currentReadiness: number
    targetReadiness: number
    projected30d: number | null
    projected90d: number | null
    maximumAchievableReadiness: number | null
    trajectory: string
    confidence: string
    certificationStatus?: string | null
    /** @deprecated use projected90d */
    projectedReadiness?: number | null
    /** @deprecated use maximumAchievableReadiness */
    expectedReadiness?: number | null
  }
  forecasting?: {
    readinessForecast?: {
      currentReadiness?: number
      slopePerDay?: number | null
      projectedReadiness30d?: number
      projectedReadiness60d?: number
      projectedReadiness90d?: number
      estimatedDaysToTarget?: number | null
      targetReadiness?: number
      confidence?: string
      confidenceReason?: string
      trajectory?: string
      historyPointCount?: number
      forecastReady?: boolean
    dataSufficiency?: {
      readinessDays?: number
      forecastReady?: boolean
      minReadinessDaysForEta?: number
      insufficientEtaMessage?: string | null
      velocityDays?: number
      controlHistoryDays?: number
      byStandardDays?: Record<string, number>
    }
    }
    dataSufficiency?: {
      readinessDays?: number
      velocityDays?: number
      controlHistoryDays?: number
      forecastReady?: boolean
      minReadinessDaysForEta?: number
      daysUntilForecastReady?: number
      insufficientEtaMessage?: string | null
      byStandardDays?: Record<string, number>
    }
    scopeMode?: 'PORTFOLIO' | 'STANDARD'
    standardCode?: string | null
    assessedStandards?: string[]
    certificationForecast?: {
      certificationStatus?: string
      estimatedReadinessDate?: string | null
      criticalBlockers?: Array<{ type: string; count: number; label: string }>
    }
    remediationForecast?: {
      velocityPerWeek?: number
      estimatedDaysToClearBacklog?: number | null
    }
    confidence?: string
    trajectory?: string
  }
  crossStandardImpactSummary?: {
    highestImpactControl?: {
      control?: string
      standardCode?: string
      totalReadinessGain?: number
      standardsAffected?: number
      impactLevel?: string
      impacts?: Array<{
        standardCode?: string
        controls?: string[]
        readinessGain?: number
        impactType?: string
      }>
    }
    highestImpactRecommendation?: {
      recommendationId?: string
      title?: string
      controlKey?: string
      estimatedGain?: number
    }
    topPriorities?: Array<{
      rank?: number
      control?: string
      gain?: number
      standardsAffected?: number
      impacts?: Array<{ standardCode?: string; readinessGain?: number }>
    }>
    totalPotentialGain?: number
    impactByControl?: Record<
      string,
      {
        totalReadinessGain?: number
        standardsAffected?: number
        impacts?: Array<{ standardCode?: string; readinessGain?: number }>
      }
    >
  }
  organizationImpactGraph?: {
    standardsAffected?: string[]
    controlsAffected?: string[]
    totalReadinessGain?: number
  }
  portfolioSummary?: {
    scopeMode?: string
    enabledStandards?: string[]
    assessedStandards?: string[]
    portfolioReadiness?: number
    portfolioVerified?: number
    executiveOpenRisks?: number
    failedControls?: number
    openRecommendations?: number
    complianceGapObservations?: number
    openComplianceGaps?: number
    standardCards?: Record<
      string,
      {
        standardCode: string
        assessed: boolean
        selfAssessed: number
        verified: number
        auditReadiness: number | null
        openGaps: number
      }
    >
  }
  complianceObservationsBreakdown?: {
    total?: number
    byStandard?: Record<string, number>
    bySourceType?: Record<string, number>
    byVisibility?: { operational_observations?: number; executive_visible?: number }
  }
  diagnostics?: {
    openComplianceGapsBreakdown?: { total?: number; byStandard?: Record<string, number> }
    internalOperationalObservationsBreakdown?: {
      total?: number
      byStandard?: Record<string, number>
      bySourceType?: Record<string, number>
    }
  }
  assessedStandards?: string[]
  requestedScope?: 'PORTFOLIO' | 'STANDARD'
  requestedStandardCode?: string | null
  updatedAt?: string
}

type Props = {
  className?: string
  data?: ExecutiveSummaryData | null
  healthStatus?: { text: string; color: string; overallScorePercent?: number } | null
}

function healthScoreColor(percent: number): string {
  if (percent >= 85) return '#34d399'
  if (percent >= 40) return '#fbbf24'
  return '#f87171'
}

function resolveVisibleStandardCodes(data: ExecutiveSummaryData) {
  const portfolioCodes = Object.keys(data.portfolioSummary?.standardCards || {})
  if (portfolioCodes.length) return portfolioCodes
  const dtoCodes = Object.keys(data.standards || {})
  return dtoCodes
}

export default function HomeExecutiveKpiPanels({
  className,
  data: externalData,
  healthStatus,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(!externalData)
  const [data, setData] = useState<ExecutiveSummaryData | null>(externalData ?? null)

  const load = useCallback(async () => {
    if (externalData) {
      setData(externalData)
      setLoading(false)
      return
    }
    if (!token) {
      setData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`${API_ENDPOINTS.GRC_EXECUTIVE_SUMMARY}?includeDiagnostics=true`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = res.ok ? await res.json().catch(() => ({})) : {}
      setData((json?.data || null) as ExecutiveSummaryData | null)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [token, externalData])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return <p className={entStyles.postureSubtitle}>{t('common.loading', 'Loading…')}</p>
  }
  if (!data) return null

  const overall =
    data.compliance?.overallCompliance ?? data.overallCompliance ?? data.readinessScore ?? 0
  const verified = data.compliance?.verifiedCompliance ?? data.verifiedCompliance ?? 0
  const auditReadiness = data.compliance?.auditReadiness ?? data.auditReadiness
  const visibleStandardCodes = resolveVisibleStandardCodes(data)

  const metric = (label: string, value: number | string, valueColor?: string) => (
    <div className={entStyles.postureMetric} key={label}>
      <p className={entStyles.postureMetricLabel}>{label}</p>
      <p className={entStyles.postureMetricValue} style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </p>
    </div>
  )

  const openComplianceCenterForStandard = (code: string) => {
    const normalized = setComplianceCenterSelectedStandard(code)
    if (!normalized || normalized === 'ALL') return
    navigate('/compliance-center/overview')
  }

  const standardBreakdownMetric = (code: string, value: string) => (
    <button
      type="button"
      key={code}
      className={`${entStyles.postureMetric} ${entStyles.postureMetricClickable}`}
      onClick={() => openComplianceCenterForStandard(code)}
      aria-label={t(
        'home.kpi.openStandardInComplianceCenter',
        'Open {{standard}} in Compliance Center',
        { standard: code },
      )}
    >
      <p className={entStyles.postureMetricLabel}>{code}</p>
      <p className={entStyles.postureMetricValue}>{value}</p>
    </button>
  )

  const portfolio = data.portfolioSummary
  const openGaps =
    data.openComplianceGaps ??
    data.openComplianceGapsBreakdown?.total ??
    data.diagnostics?.openComplianceGapsBreakdown?.total ??
    portfolio?.openComplianceGaps ??
    0
  const assessedList = portfolio?.assessedStandards?.length
    ? portfolio.assessedStandards.join(', ')
    : data.assessedStandards?.join(', ') || '—'
  const enabledList = portfolio?.enabledStandards?.length
    ? portfolio.enabledStandards.join(', ')
    : visibleStandardCodes.join(', ') || '—'

  return (
    <div className={className} data-testid="home-executive-kpi-panels">
      {healthStatus ? (
        <div style={{ marginBottom: 16 }}>
          <div className={entStyles.postureGrid}>
            {metric(t('labels.status', 'Status'), healthStatus.text, healthStatus.color)}
            {healthStatus.overallScorePercent != null
              ? metric(
                  t('home.totalSecurityHealth.overallScore', 'Overall score'),
                  `${Math.round(healthStatus.overallScorePercent)}%`,
                  healthScoreColor(healthStatus.overallScorePercent),
                )
              : null}
          </div>
        </div>
      ) : null}
      {portfolio ? (
        <div style={{ marginBottom: 16 }}>
          <p className={entStyles.layerLabel}>{t('home.kpi.portfolio', 'Portfolio summary')}</p>
          <p className={entStyles.postureSubtitle}>
            {t('home.kpi.enabledStandards', 'Active standards')}: {enabledList}
          </p>
          <p className={entStyles.postureSubtitle}>
            {t('home.kpi.assessedStandards', 'Assessed standards')}: {assessedList}
          </p>
          <div className={entStyles.postureGrid}>
            {metric(t('home.kpi.portfolioReadiness', 'Portfolio readiness'), `${portfolio.portfolioReadiness ?? overall}%`)}
            {metric(t('home.kpi.portfolioVerified', 'Portfolio verified'), `${portfolio.portfolioVerified ?? verified}%`)}
            {auditReadiness != null
              ? metric(t('home.kpi.auditReadiness', 'Audit readiness'), `${auditReadiness}%`)
              : null}
          </div>
        </div>
      ) : null}
      {portfolio?.standardCards ? (
        <div style={{ marginBottom: 16 }}>
          <p className={entStyles.layerLabel}>{t('home.kpi.standardsBreakdown', 'Standards breakdown')}</p>
          <div className={entStyles.postureGrid}>
            {visibleStandardCodes.map((code) => {
              const card = portfolio.standardCards?.[code]
              if (!card) return null
              const label = card.assessed
                ? `${code}: ${card.selfAssessed}% self · ${card.verified}% verified · ${card.openGaps} gaps`
                : `${code}: ${t('home.kpi.notAssessed', 'Not assessed')}`
              return standardBreakdownMetric(code, label)
            })}
          </div>
        </div>
      ) : null}
      <div style={{ marginBottom: 16 }}>
        <p className={entStyles.layerLabel}>{t('home.kpi.compliance', 'Compliance KPIs')}</p>
        <div className={entStyles.postureGrid}>
          {metric(t('home.kpi.failedControls', 'Failed controls'), data.failedControls)}
          {metric(t('home.kpi.auditGaps', 'Audit gaps'), data.auditGaps)}
          {metric(t('home.kpi.openRecommendations', 'Open recommendations'), data.openRecommendations)}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <p className={entStyles.layerLabel}>{t('home.kpi.risks', 'Risk KPIs')}</p>
        <div className={entStyles.postureGrid}>
          {metric(
            t('home.kpi.executiveOpenRisks', 'Executive open risks'),
            data.executiveOpenRisks ?? data.openRisks,
          )}
          {metric(t('home.kpi.openComplianceGaps', 'Open compliance gaps'), openGaps)}
          {metric(t('home.kpi.criticalRisks', 'Critical risks'), data.criticalRisks)}
          {metric(
            t('home.kpi.highPriorityRisks', 'High priority risks'),
            data.highOrCriticalRisks ?? data.criticalRisks,
          )}
        </div>
      </div>
    </div>
  )
}
